import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  MessageCircle, 
  Users, 
  Trophy, 
  Gamepad2, 
  Sparkles,
  Clock,
  CalendarCheck,
  PartyPopper,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Instagram,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

const WHATSAPP_NUMBER = "491741944996";

const getWhatsAppLink = (message) => {
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
};

// TikTok Icon Component
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Navigation for subpage
const BirthdayNavigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openWhatsApp = () => {
    window.open(getWhatsAppLink("Hallo, ich möchte einen Fußball-Geburtstag anfragen."), "_blank");
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5 ${
        isScrolled ? "py-3" : "py-4"
      } transition-all duration-300`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo + Back Link */}
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-3"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_coach-reserve-2/artifacts/rhhlwg7u_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo%20%282%29.png" 
                alt="Aktas Personal Fussballakademie Logo"
                className="h-12 w-12 object-contain"
              />
              <div className="hidden sm:block">
                <p className="font-['Bebas_Neue'] text-lg text-white leading-tight">
                  YASIN AKTAS
                </p>
                <p className="text-[10px] text-[#A1A1AA] tracking-[0.2em]">
                  PERSONAL FUSSBALLTRAINING
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Hauptseite
            </Link>
            
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/fussballtraining_aktas"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#A1A1AA] hover:text-[#c1ff72] transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://tiktok.com/@fussballtraining_aktas"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#A1A1AA] hover:text-[#c1ff72] transition-colors"
              >
                <TikTokIcon size={20} />
              </a>
            </div>
            
            <Button
              onClick={openWhatsApp}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold px-6 py-5 rounded-none transition-all duration-300"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              JETZT ANFRAGEN
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Hauptseite
              </Link>
              <Button
                onClick={openWhatsApp}
                className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold px-6 py-4 rounded-none w-full"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                JETZT ANFRAGEN
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const openWhatsApp = () => {
    window.open(getWhatsAppLink("Hallo, ich möchte einen Fußball-Geburtstag anfragen."), "_blank");
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1920&q=80')`,
        }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/70 to-[#0A0A0A]" />
      {/* Green Accent Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#c1ff72]/10 via-transparent to-transparent" />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[#c1ff72]/10 border border-[#c1ff72]/30 px-4 py-2 mb-8 backdrop-blur-sm">
          <PartyPopper className="w-5 h-5 text-[#c1ff72]" />
          <span className="text-[#c1ff72] text-sm font-medium uppercase tracking-wider">
            Kindergeburtstag
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tight text-white mb-6 leading-[0.9] drop-shadow-lg">
          FUSSBALL-GEBURTSTAG<br />
          <span className="text-[#c1ff72]">FÜR JUNGE CHAMPIONS</span>
        </h1>

        {/* Subheadline */}
        <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed drop-shadow-md">
          Ein besonderer Geburtstag mit Fußball, Spaß, Bewegung und echten Trainingseinheiten.
        </p>

        {/* CTA Button */}
        <Button
          onClick={openWhatsApp}
          className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-bold px-10 py-8 rounded-none text-lg transition-all duration-300 hover:shadow-[0_0_50px_rgba(193,255,114,0.5)] hover:scale-105"
          data-testid="hero-cta-button"
        >
          <MessageCircle className="w-6 h-6 mr-3" />
          GEBURTSTAG PER WHATSAPP ANFRAGEN
          <ChevronRight className="w-6 h-6 ml-2" />
        </Button>

        {/* Trust indicators */}
        <p className="mt-8 text-white/70 text-sm">
          Schnelle Antwort • Flexible Termine • Unvergessliches Erlebnis
        </p>
      </div>
    </section>
  );
};

// Birthday Collage Section - Emotional First Impression
const CollageSection = () => {
  return (
    <section className="py-12 lg:py-16 bg-[#0A0A0A]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative group">
          {/* Main Collage Image */}
          <div className="relative overflow-hidden rounded-lg">
            <img
              src="https://customer-assets.emergentagent.com/job_coach-reserve-2/artifacts/6ioutv27_ChatGPT%20Image%208.%20Mai%202026%2C%2021_44_33.png"
              alt="Fußball-Geburtstag Collage - Kinder beim Training, Teamspiele und Celebration"
              className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
              loading="eager"
            />
            {/* Subtle overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            {/* Green border accent on hover */}
            <div className="absolute inset-0 border-2 border-[#c1ff72]/0 group-hover:border-[#c1ff72]/30 rounded-lg transition-all duration-500" />
          </div>
          
          {/* Caption */}
          <p className="text-center text-[#71717A] text-sm mt-4">
            Echte Eindrücke von unseren Fußball-Geburtstagen
          </p>
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Gamepad2,
      title: "Spaß & Bewegung",
      description: "Aktive Spiele und Übungen, die allen Kindern Freude machen und für ordentlich Bewegung sorgen."
    },
    {
      icon: Trophy,
      title: "Professionelles Training",
      description: "Echte Trainingseinheiten mit einem lizenzierten Trainer – wie bei den Profis."
    },
    {
      icon: Users,
      title: "Teamspiele & Challenges",
      description: "Spannende Wettkämpfe und Teamspiele, die den Teamgeist fördern und für Spannung sorgen."
    },
    {
      icon: Sparkles,
      title: "Unvergessliches Erlebnis",
      description: "Ein Geburtstag, von dem die Kinder noch lange erzählen werden – aktiv, motivierend und besonders."
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-4">
            WARUM EIN <span className="text-[#c1ff72]">FUSSBALL-GEBURTSTAG</span>?
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Ein Geburtstag, der Kinder begeistert und in Bewegung bringt
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="bg-[#1A1A1A] border border-white/5 p-8 hover:border-[#c1ff72]/30 transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-[#c1ff72]/10 flex items-center justify-center mb-6 group-hover:bg-[#c1ff72]/20 transition-colors">
                <benefit.icon className="w-7 h-7 text-[#c1ff72]" />
              </div>
              <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-3">
                {benefit.title}
              </h3>
              <p className="text-[#A1A1AA] leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Image Gallery Section - Between Benefits and Packages
const ImageGallerySection = () => {
  const images = [
    {
      src: "https://images.unsplash.com/photo-1772707763538-8532d8387ab4?w=600&q=80",
      alt: "Fußball Training",
      span: "col-span-1"
    },
    {
      src: "https://images.unsplash.com/photo-1710789020364-a76c9517ea7e?w=600&q=80",
      alt: "Kinder Team Celebration",
      span: "col-span-1"
    },
    {
      src: "https://images.unsplash.com/photo-1652190416554-c46af8a0ff50?w=800&q=80",
      alt: "Fußball Tor",
      span: "col-span-1 md:col-span-2"
    },
    {
      src: "https://images.unsplash.com/photo-1701872324421-f537bc8f61de?w=600&q=80",
      alt: "Kinder spielen Fußball",
      span: "col-span-1"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-[#c1ff72] text-sm font-medium uppercase tracking-wider mb-2">
            Eindrücke
          </p>
          <h2 className="font-['Bebas_Neue'] text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight text-white">
            TRAINING, SPASS & <span className="text-[#c1ff72]">TEAMGEIST</span>
          </h2>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {images.map((image) => (
            <div
              key={image.alt}
              className={`${image.span} relative aspect-square overflow-hidden group`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {/* Green border on hover */}
              <div className="absolute inset-0 border-2 border-[#c1ff72]/0 group-hover:border-[#c1ff72]/50 transition-all duration-300" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Packages Section
const PackagesSection = () => {
  const packages = [
    {
      name: "Paket 1",
      players: "bis 10 Spieler",
      duration: "1 Std.",
      price: "190",
      features: [
        "Professionelles Training",
        "Teamspiele & Challenges",
        "T-Shirt mit FA-Logo",
        "Getränke & Süßigkeiten"
      ]
    },
    {
      name: "Paket 2",
      players: "bis 10 Spieler",
      duration: "1,5 Std.",
      price: "235",
      features: [
        "Professionelles Training",
        "Teamspiele & Challenges",
        "T-Shirt mit FA-Logo",
        "Getränke & Süßigkeiten"
      ],
      popular: true
    },
    {
      name: "Paket 3",
      players: "bis 15 Spieler",
      duration: "1,5 Std.",
      price: "270",
      features: [
        "Professionelles Training",
        "Teamspiele & Challenges",
        "T-Shirt mit FA-Logo",
        "Getränke & Süßigkeiten"
      ]
    },
    {
      name: "Paket 4",
      players: "bis 20 Spieler",
      duration: "1,5 Std.",
      price: "300",
      features: [
        "Professionelles Training",
        "Teamspiele & Challenges",
        "T-Shirt mit FA-Logo",
        "Getränke & Süßigkeiten"
      ]
    }
  ];

  const handlePackageClick = (packageName) => {
    window.open(getWhatsAppLink(`Hallo, ich interessiere mich für ${packageName} für einen Fußball-Geburtstag.`), "_blank");
  };

  return (
    <section className="py-20 lg:py-28 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-4">
            GEBURTSTAGS<span className="text-[#c1ff72]">PAKETE</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Wähle das passende Paket für deine Feier
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.name}
              className={`relative bg-[#1A1A1A] border ${
                pkg.popular ? 'border-[#c1ff72]' : 'border-white/5'
              } p-6 flex flex-col`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c1ff72] text-[#0A0A0A] text-xs font-bold px-4 py-1 uppercase tracking-wider">
                  Beliebt
                </div>
              )}

              {/* Package Header */}
              <div className="text-center mb-6 pt-2">
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">
                  {pkg.name}
                </h3>
                <p className="text-[#c1ff72] font-medium">
                  {pkg.players}
                </p>
                <p className="text-[#A1A1AA] text-sm flex items-center justify-center gap-1 mt-1">
                  <Clock className="w-4 h-4" />
                  {pkg.duration}
                </p>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <span className="font-['Bebas_Neue'] text-5xl text-white">{pkg.price}</span>
                <span className="text-[#A1A1AA] text-xl ml-1">€</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-grow">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-[#A1A1AA]">
                    <ChevronRight className="w-4 h-4 text-[#c1ff72] mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={() => handlePackageClick(pkg.name)}
                className={`w-full rounded-none py-6 font-semibold transition-all duration-300 ${
                  pkg.popular
                    ? 'bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A]'
                    : 'bg-transparent border-2 border-[#c1ff72] text-white hover:bg-[#c1ff72] hover:text-[#0A0A0A]'
                }`}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                PER WHATSAPP ANFRAGEN
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: MessageCircle,
      title: "Anfrage senden",
      description: "Schreib mir kurz per WhatsApp deinen Wunschtermin und die Anzahl der Kinder."
    },
    {
      number: "02",
      icon: CalendarCheck,
      title: "Termin abstimmen",
      description: "Wir klären gemeinsam Ort, Uhrzeit und das passende Paket."
    },
    {
      number: "03",
      icon: PartyPopper,
      title: "Fußball-Geburtstag erleben",
      description: "Die Kinder erleben ein sportliches, motivierendes und unvergessliches Training."
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-[#0A0A0A]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-4">
            SO EINFACH <span className="text-[#c1ff72]">LÄUFT&apos;S AB</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            In nur drei Schritten zum unvergesslichen Fußball-Geburtstag
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div key={step.number} className="relative">
              {/* Connector Line (hidden on mobile and last item) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-[#c1ff72]/50 to-transparent" />
              )}
              
              <div className="text-center">
                {/* Number Badge */}
                <div className="inline-flex items-center justify-center w-24 h-24 bg-[#1A1A1A] border-2 border-[#c1ff72] mb-6 relative">
                  <span className="font-['Bebas_Neue'] text-4xl text-[#c1ff72]">
                    {step.number}
                  </span>
                </div>
                
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-4">
                  {step.title}
                </h3>
                
                <p className="text-[#A1A1AA] leading-relaxed max-w-xs mx-auto">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Wo findet der Geburtstag statt?",
      answer: "Der Fußball-Geburtstag kann auf verschiedenen Plätzen in der Region stattfinden. Wir besprechen gemeinsam den idealen Ort – entweder ein lokaler Sportplatz, eine Halle oder ein anderer geeigneter Standort in deiner Nähe."
    },
    {
      question: "Wie viele Kinder können teilnehmen?",
      answer: "Je nach Paket können zwischen 10 und 20 Kinder teilnehmen. Bei größeren Gruppen kontaktiere mich gerne für ein individuelles Angebot."
    },
    {
      question: "Was müssen die Kinder mitbringen?",
      answer: "Die Kinder sollten Sportkleidung und Fußballschuhe oder Turnschuhe mitbringen. Schienbeinschoner sind empfohlen, aber nicht zwingend erforderlich. Getränke und Snacks sind im Paket bereits enthalten."
    },
    {
      question: "Ist das Training für Anfänger geeignet?",
      answer: "Absolut! Das Training wird an das Alter und die Fähigkeiten der Kinder angepasst. Ob Anfänger oder bereits erfahrene Spieler – alle Kinder werden Spaß haben und etwas lernen."
    },
    {
      question: "Wie frage ich einen Termin an?",
      answer: "Schreib mir einfach per WhatsApp mit deinem Wunschtermin, der Anzahl der Kinder und dem gewünschten Paket. Ich antworte in der Regel innerhalb weniger Stunden und wir klären alle Details."
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-[#121212]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-4">
            HÄUFIGE <span className="text-[#c1ff72]">FRAGEN</span>
          </h2>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.question}
              className="bg-[#1A1A1A] border border-white/5"
            >
              <button
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="font-medium text-white pr-4">{faq.question}</span>
                <ChevronDown
                  className={`w-5 h-5 text-[#c1ff72] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-[#A1A1AA] leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Image Banner Section - Above Final CTA
const ImageBannerSection = () => {
  return (
    <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1671209151455-86980f5bf293?w=1920&q=80')`,
        }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 via-[#0A0A0A]/60 to-[#0A0A0A]/90" />
      
      {/* Content */}
      <div className="relative h-full flex items-center justify-center">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#c1ff72] text-sm font-medium uppercase tracking-wider mb-4">
            Das perfekte Geschenk
          </p>
          <h3 className="font-['Bebas_Neue'] text-4xl md:text-5xl lg:text-6xl uppercase text-white mb-4">
            EIN GEBURTSTAG, DEN DIE KINDER<br />
            <span className="text-[#c1ff72]">NICHT VERGESSEN WERDEN</span>
          </h3>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Bewegung, Teamgeist und jede Menge Spaß – der perfekte Tag für junge Fußball-Fans.
          </p>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const openWhatsApp = () => {
    window.open(getWhatsAppLink("Hallo, ich möchte einen Fußball-Geburtstag anfragen."), "_blank");
  };

  return (
    <section className="py-20 lg:py-28 bg-[#0A0A0A] relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#c1ff72]/10 via-transparent to-transparent" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight text-white mb-6">
          BEREIT FÜR EINEN<br />
          <span className="text-[#c1ff72]">UNVERGESSLICHEN FUSSBALL-GEBURTSTAG</span>?
        </h2>
        
        <p className="text-xl text-[#A1A1AA] max-w-2xl mx-auto mb-10 leading-relaxed">
          Schreib mir direkt auf WhatsApp und wir planen gemeinsam den passenden Geburtstag.
        </p>
        
        <Button
          onClick={openWhatsApp}
          className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-bold px-12 py-8 rounded-none text-lg transition-all duration-300 hover:shadow-[0_0_50px_rgba(193,255,114,0.5)] hover:scale-105"
          data-testid="final-cta-button"
        >
          <MessageCircle className="w-6 h-6 mr-3" />
          JETZT GEBURTSTAG ANFRAGEN
          <ChevronRight className="w-6 h-6 ml-2" />
        </Button>
        
        <p className="mt-8 text-[#71717A] text-sm">
          Schnelle Antwort • Flexible Termine • Persönliche Beratung
        </p>
      </div>
    </section>
  );
};

// Footer for subpage
const BirthdayFooter = () => {
  return (
    <footer className="py-10 bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_coach-reserve-2/artifacts/ba2pyiiu_E8675A27-AB74-4AD8-A47C-0CC6F6A992C1.PNG" 
              alt="Aktas Personal Fussballtraining Logo"
              className="h-10 w-10 object-contain"
            />
            <div>
              <p className="font-['Bebas_Neue'] text-white">YASIN AKTAS</p>
              <p className="text-[#71717A] text-xs">Personal Fußballtraining</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <a
              href="https://instagram.com/fussballtraining_aktas"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A1A1AA] hover:text-[#c1ff72] transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://tiktok.com/@fussballtraining_aktas"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A1A1AA] hover:text-[#c1ff72] transition-colors"
            >
              <TikTokIcon size={20} />
            </a>
          </div>
          
          <div className="text-center md:text-right">
            <Link
              to="/"
              className="text-[#A1A1AA] hover:text-white transition-colors text-sm"
            >
              Zurück zur Hauptseite
            </Link>
            <p className="text-[#71717A] text-xs mt-2">
              © {new Date().getFullYear()} Yasin Aktas. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Birthday Page Component
export default function BirthdayPage() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className={`transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <SEO 
        title="Fußball Kindergeburtstag | Personal Fußballtraining"
        description="Unvergessliche Fußball-Kindergeburtstage mit professionellem Training, Spielen und jeder Menge Spaß. Jetzt buchen für ein einzigartiges Erlebnis!"
        path="/geburtstag"
      />
      <BirthdayNavigation />
      <HeroSection />
      <CollageSection />
      <BenefitsSection />
      <PackagesSection />
      <HowItWorksSection />
      <FAQSection />
      <ImageBannerSection />
      <FinalCTASection />
      <BirthdayFooter />
    </main>
  );
}
